/**
 * AJUSTES — seus dados, categorias e padrões para fichas NOVAS.
 * Nada aqui muda ficha já feita: cada ficha copia os padrões quando nasce.
 */
import { useRef, useState } from 'react'
import { baixar } from '../dados/arquivos'
import { lerEstado, mudar, substituirTudo, usarEstado, type Estado } from '../dados/armazem'
import { agora, hoje, lerNumero, paraCampo, quando } from '../dominio/numeros'
import type { Ajustes as Aj, Base } from '../dominio/tipos'
import { problemaDoRendimento, rendimentoPadrao } from '../dominio/contas'
import { FERRAMENTAS, ferramentaLigada, type NomeDaFerramenta } from '../dominio/ferramentas'
import { api, atualizarConta, Recusa, sair, SemSinal, type Conta, type Eu } from '../dados/sessao'
import { avisar, Campo, confirmar, Dica, Escolha, type Ir } from '../ui'
import { pode } from '../dominio/permissoes'

export function Ajustes({ eu, ir }: { eu: Eu; ir: Ir }) {
  const { ajustes, fichas, produtores } = usarEstado()
  const [novaCat, setNovaCat] = useState('')
  const arquivo = useRef<HTMLInputElement>(null)
  const muda = (fn: (a: Aj) => void) => mudar((e) => fn(e.ajustes))
  // 3E: categorias e padrões só servem para abrir ficha; a Consulta não abre
  const podePadroes = pode(eu.pessoa.papel, 'padroesDoAparelho')
  const pct = (k: 'rendimento' | 'financeiroPct' | 'funruralPct', padrao: number) => (v: string) =>
    muda((a) => { a[k] = lerNumero(v) ?? padrao })

  function acrescentar() {
    const c = novaCat.trim()
    if (!c) return
    if (ajustes.categorias.some((x) => x.toLowerCase() === c.toLowerCase())) { avisar('Essa categoria já existe'); return }
    muda((a) => { a.categorias.push(c) })
    setNovaCat('')
  }

  function salvarCopia() {
    mudar((e) => { e.ajustes.ultimaCopia = agora() })
    baixar(JSON.stringify({ app: 'pesagem', versao: 1, geradoEm: agora(), dados: lerEstado() }), `pesagem-copia-${hoje()}.json`, 'application/json')
    avisar('Cópia salva em Downloads')
  }

  function restaurar(a: File | undefined) {
    if (!a) return
    a.text().then((t) => {
      let d: { app?: string; geradoEm?: string; dados?: Estado }
      try { d = JSON.parse(t) } catch { window.alert('Não consegui ler esse arquivo.'); return }
      if (d.app !== 'pesagem' || !d.dados) { window.alert('Esse arquivo não é uma cópia do Pesagem.'); return }
      const atual = lerEstado(), src = d.dados
      // junta: nada é apagado; ficha de mesmo id fica com a alterada por último
      const fichasJ = [...atual.fichas]
      let novas = 0, atualizadas = 0
      for (const f of src.fichas ?? []) {
        const i = fichasJ.findIndex((x) => x.id === f.id)
        if (i < 0) { fichasJ.push(f); novas++ } else if (f.alteradaEm > fichasJ[i]!.alteradaEm) { fichasJ[i] = f; atualizadas++ }
      }
      const prods = [...atual.produtores]
      for (const p of src.produtores ?? []) {
        const i = prods.findIndex((x) => x.id === p.id)
        if (i < 0) prods.push(p); else if (p.alteradoEm > prods[i]!.alteradoEm) prods[i] = p
      }
      // R2B: plural certo («1 ficha nova», «2 fichas novas»)
      const contar = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`
      if (!window.confirm(`A cópia de ${quando(d.geradoEm)} traz ${contar(novas, 'ficha nova', 'fichas novas')} e ${contar(atualizadas, 'mais atualizada', 'mais atualizadas')}.\n\nJuntar com o que está aqui? Nada será apagado.`)) return
      const seq = { ...atual.seq }
      for (const [k, v] of Object.entries(src.seq ?? {})) seq[k] = Math.max(seq[k] ?? 0, v)
      substituirTudo({ ...atual, fichas: fichasJ, produtores: prods, seq })
      avisar('Cópia restaurada')
    })
  }

  return (
    <div>
      <Voce eu={eu} ir={ir} />
      <DadosDaEmpresa eu={eu} />
      <Ferramentas eu={eu} />

      <h2 className="secao">Categorias</h2>
      <div className="cartao">
        <div className="categorias">
          {ajustes.categorias.map((c) => (
            <span key={c} className="categoria">{c}
              {podePadroes && <button onClick={async () => {
                if (ajustes.categorias.length > 1 && await confirmar({ titulo: `Tirar ${c} da lista?`, texto: 'Fichas antigas não mudam.', sim: 'Tirar da lista' })) muda((a) => { a.categorias = a.categorias.filter((x) => x !== c) })
              }} aria-label={`tirar ${c}`}>×</button>}</span>
          ))}
        </div>
        {podePadroes && <div className="linha fim">
          <div style={{ flex: 1 }}><Campo rotulo="Nova categoria" valor={novaCat} mudar={setNovaCat} dica="ex.: Vaca magra, Misto" /></div>
          <button className="mini alto" onClick={acrescentar}>+ Acrescentar</button>
        </div>}
      </div>

      <h2 className="secao">Padrões para fichas novas</h2>
      <div className="cartao">
        <fieldset disabled={!podePadroes} className="sem-borda">
        <Escolha<Base> rotulo="Base do preço" valor={ajustes.base} mudar={(v) => muda((a) => { a.base = v })}
          opcoes={[['arroba', 'Por arroba (@)'], ['kg', 'Por kg de peso vivo'], ['cabeca', 'Por cabeça']]} />
        {/* R1C: rendimento por categoria — o lote novo de Vaca já nasce com o da vaca */}
        <div className="campo"><span>Rendimento de carcaça por categoria (%)</span></div>
        <div className="rendimentos">
          {ajustes.categorias.map((c) => (
            <RendimentoDaCategoria key={c} categoria={c} valor={rendimentoPadrao(c, ajustes)}
              mudar={(v) => muda((a) => { a.rendimentos = { ...(a.rendimentos ?? {}), [c]: v } })} />
          ))}
        </div>
        <CampoPct rotulo="Rendimento das outras categorias (%)" valor={ajustes.rendimento} mudar={(v) => {
          const n = lerNumero(v)
          if (problemaDoRendimento(n)) { avisar(problemaDoRendimento(n)!); return }
          muda((a) => { a.rendimento = n! })
        }} />
        <div className="linha">
          <CampoPct rotulo="Desconto à vista (financeiro) %" valor={ajustes.financeiroPct} mudar={pct('financeiroPct', 0)} />
          <CampoPct rotulo="Desconto de Funrural (repasse do frigorífico) %" valor={ajustes.funruralPct} mudar={pct('funruralPct', 0)} />
        </div>
        <Dica>Estes valores só preenchem a negociação de uma ficha <b>nova</b>. Mudar aqui <b>nunca</b> altera fichas nem romaneios já feitos.
          Os descontos vão para o primeiro lote; os lotes seguintes copiam os do lote anterior, e cada lote pode ter os seus.
          O rendimento aceito vai de 40 a 60% (o normal é de 48 a 56%).</Dica>
        </fieldset>
      </div>

      <h2 className="secao">Aparência</h2>
      <div className="cartao">
        <div className="linha fim">
          <Escolha rotulo="Tema" valor={ajustes.tema} mudar={(v) => muda((a) => { a.tema = v })}
            opcoes={[['auto', 'Automático (segue o celular)'], ['claro', 'Claro'], ['escuro', 'Escuro']]} />
          <BotaoDeTema />
        </div>
        <Dica>No sol do curral, o tema claro costuma ser mais fácil de ler. O botão ☀/🌙 também fica na tela de pesagem.</Dica>
      </div>

      <h2 className="secao">Cópia de segurança</h2>
      <div className="cartao">
        {/* R2B: o texto antigo («até o banco de dados entrar») era da etapa 1 — o banco já entrou */}
        <div>O que vale está no <b>banco</b>: o «Sincronizar» guarda lá. Esta cópia é uma garantia a mais, deste aparelho:
          leva as <b>fichas</b> (pesagens, lotes, porteiras, parcelas combinadas e gastos), os <b>produtores</b> e os <b>Ajustes</b>.
          Não leva os comprovantes (fotos e PDFs), os pagamentos lançados, as comissões, o extrato, o Meu estoque nem as notas — esses vivem no banco.</div>
        <div className="mini-dica">{ajustes.ultimaCopia ? 'Última cópia: ' + quando(ajustes.ultimaCopia) : 'Nenhuma cópia feita ainda.'}</div>
        <button className="botao" onClick={salvarCopia}>Salvar cópia agora</button>
        {pode(eu.pessoa.papel, 'restaurarCopia') && <button className="botao fantasma" onClick={() => arquivo.current?.click()}>Restaurar de uma cópia</button>}
        <input ref={arquivo} type="file" accept=".json,application/json" hidden onChange={(e) => { restaurar(e.target.files?.[0]); e.target.value = '' }} />
        <Dica>Restaurar junta a cópia com o que já está aqui: nada é apagado. Os comprovantes anexados não entram na cópia.</Dica>
      </div>
      <div className="mini-dica" style={{ textAlign: 'center', margin: '18px 0' }}>Pesagem · {fichas.length} ficha{fichas.length === 1 ? '' : 's'} e {produtores.length} produtor{produtores.length === 1 ? '' : 'es'} neste aparelho</div>
    </div>
  )
}

/**
 * R1C · ☀/🌙 — troca entre o tema claro e o escuro num toque (na Pesagem e
 * aqui). No sol do curral o claro lê melhor; a escolha fica guardada no
 * aparelho, como o tema dos Ajustes.
 */
export function BotaoDeTema({ compacto = false }: { compacto?: boolean }) {
  const { ajustes } = usarEstado()
  const escuroAgora = ajustes.tema === 'escuro'
    || (ajustes.tema === 'auto' && !(typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches))
  return (
    <button type="button" className="mini alto botao-tema" aria-label={escuroAgora ? 'Usar o tema claro' : 'Usar o tema escuro'}
      title={escuroAgora ? 'Tema claro' : 'Tema escuro'}
      onClick={() => mudar((e) => { e.ajustes.tema = escuroAgora ? 'claro' : 'escuro' })}>
      {compacto ? (escuroAgora ? '☀' : '🌙') : escuroAgora ? '☀ Claro' : '🌙 Escuro'}
    </button>
  )
}

/** R1C · O rendimento de uma categoria: fora de 40 a 60%, aviso vermelho e não grava. */
function RendimentoDaCategoria({ categoria, valor, mudar: gravar }: { categoria: string; valor: number; mudar: (v: number) => void }) {
  const [t, setT] = useState(paraCampo(valor))
  const n = lerNumero(t)
  const problema = problemaDoRendimento(n)
  return (
    <label className="campo rendimento-cat"><span>{categoria}</span>
      <input inputMode="decimal" value={t} onChange={(e) => setT(e.target.value)} onBlur={() => { if (!problema && n !== valor) gravar(n!) }} />
      {problema && <div className="alerta">{problema}</div>}
    </label>
  )
}

/** Campo de percentual que só grava ao sair do campo — senão «1,» viraria 1 enquanto se digita. */
function CampoPct(p: { rotulo: string; valor: number; mudar: (v: string) => void }) {
  const [t, setT] = useState(paraCampo(p.valor || null))
  return (
    <label className="campo"><span>{p.rotulo}</span>
      <input inputMode="decimal" value={t} placeholder="0" onChange={(e) => setT(e.target.value)} onBlur={() => p.mudar(t)} /></label>
  )
}

const NOME_DO_PAPEL = { gestor: 'Gestor', comprador: 'Comprador', consulta: 'Consulta' } as const

function Voce({ eu, ir }: { eu: Eu; ir: Ir }) {
  const p = eu.pessoa
  return (
    <>
      <h2 className="secao">Você</h2>
      <div className="cartao">
        <div className="nome">{p.nome}</div>
        <div className="mini-dica">{p.email} · {NOME_DO_PAPEL[p.papel]}{p.plataforma ? ' · Plataforma' : ''} · sigla {p.sigla}</div>
        <div className="mini-dica">Empresa: {eu.conta.nome_fantasia}</div>
        {pode(p.papel, 'verEquipe') && <button className="botao fantasma" onClick={() => ir({ tela: 'equipe' })}>Equipe</button>}
        {p.plataforma && <button className="botao fantasma" onClick={() => ir({ tela: 'plataforma' })}>Plataforma — empresas</button>}
        <button className="botao fantasma" onClick={async () => {
          if (await confirmar({ titulo: 'Sair deste aparelho?', texto: 'Para voltar, você vai precisar da senha (e de internet).', sim: 'Sair', perigo: true })) sair()
        }}>Sair deste aparelho</button>
      </div>
    </>
  )
}

/**
 * 3C · Ajustes › Ferramentas (decisão do gestor, 06/10/2026). O app abre «só
 * pesagem»; cada ferramenta a mais o gestor liga aqui, para a empresa toda.
 * Desligar só esconde: nada se apaga, e religar traz tudo de volta. Mudar
 * precisa de sinal (é da empresa, não do aparelho); valer, não — a escolha
 * fica guardada no aparelho junto com o «eu».
 */
function Ferramentas({ eu }: { eu: Eu }) {
  const gestor = pode(eu.pessoa.papel, 'ferramentas')
  // antes da migração 011 o servidor não manda o campo: tudo ligado, sem como desligar
  const semMigracao = !eu.conta.ferramentas
  const [indo, setIndo] = useState<NomeDaFerramenta | null>(null)

  async function trocar(nome: NomeDaFerramenta, ligar: boolean) {
    const t = FERRAMENTAS.find((f) => f.nome === nome)!.titulo
    if (!ligar && !window.confirm(`Desligar «${t}»?\n\nSó esconde a ferramenta para toda a equipe. Nada é apagado: ao religar, está tudo lá.`)) return
    setIndo(nome)
    try {
      const atuais = Object.fromEntries(FERRAMENTAS.map((f) => [f.nome, ferramentaLigada(eu.conta, f.nome)]))
      const r = await api<{ conta: Conta }>('conta/ferramentas', { ferramentas: { ...atuais, [nome]: ligar } })
      atualizarConta(r.conta)
      avisar(`${t}: ${ligar ? 'ligada' : 'desligada'}`)
    } catch (e) {
      window.alert(e instanceof SemSinal ? 'Sem internet agora. Ligar e desligar ferramentas precisa de sinal.' : e instanceof Recusa ? e.message : 'Não deu para salvar.')
    } finally { setIndo(null) }
  }

  return (
    <>
      <h2 className="secao">Ferramentas</h2>
      <div className="cartao">
        <div className="mini-dica" style={{ marginTop: 0 }}>A pesagem está sempre ligada: ficha, lotes, porteira, pesagem e romaneio (PDF, Excel e WhatsApp).
          As ferramentas abaixo aparecem para toda a equipe quando o gestor liga.</div>
        {FERRAMENTAS.map((f) => {
          const ligada = ferramentaLigada(eu.conta, f.nome)
          return (
            <div key={f.nome} className="item">
              <label className="item-topo" style={{ alignItems: 'center', cursor: gestor && !semMigracao ? 'pointer' : 'default' }}>
                <span className="item-titulo">
                  <input type="checkbox" checked={ligada} disabled={!gestor || semMigracao || indo !== null}
                    onChange={(e) => trocar(f.nome, e.target.checked)} /> {f.titulo}
                </span>
                <span className={`etiqueta ${ligada ? 'ok' : 'neutra'}`}>{indo === f.nome ? 'salvando…' : ligada ? 'ligada' : 'desligada'}</span>
              </label>
              <div className="mini-dica">{f.frase}</div>
            </div>
          )
        })}
        <Dica>{semMigracao
          ? 'Por enquanto todas ficam ligadas: o banco ainda não recebeu a atualização 011. Depois dela, o gestor liga e desliga aqui.'
          : gestor
            ? 'Desligar só esconde: nada é apagado e os dados continuam sincronizando. Religou, está tudo lá. Precisa de internet para mudar.'
            : 'Só o gestor liga e desliga as ferramentas.'}</Dica>
      </div>
    </>
  )
}

/** Os dados da empresa: vêm do servidor e saem no romaneio. Só o gestor muda. */
function DadosDaEmpresa({ eu }: { eu: Eu }) {
  const gestor = pode(eu.pessoa.papel, 'dadosDaEmpresa')
  const [c, setC] = useState<Conta>(eu.conta)
  const [indo, setIndo] = useState(false)
  const campo = (k: keyof Conta) => (v: string) => setC({ ...c, [k]: v })
  async function salvar() {
    setIndo(true)
    try {
      const r = await api<{ conta: Conta }>('conta', c)
      atualizarConta(r.conta)
      avisar('Dados da empresa salvos')
    } catch (e) {
      window.alert(e instanceof SemSinal ? 'Sem internet agora. Tente de novo com sinal.' : e instanceof Recusa ? e.message : 'Não deu para salvar.')
    } finally { setIndo(false) }
  }
  return (
    <>
      <h2 className="secao">Empresa (sai no romaneio)</h2>
      <div className="cartao">
        <fieldset disabled={!gestor} className="sem-borda">
          <Campo rotulo="Nome fantasia" valor={c.nome_fantasia} mudar={campo('nome_fantasia')} dica="ex.: Peteca Compra de Gado" />
          <Campo rotulo="Razão social (ou seu nome)" valor={c.razao_social} mudar={campo('razao_social')} />
          <div className="linha">
            <Campo rotulo="CPF/CNPJ" valor={c.doc} mudar={campo('doc')} modo="numeric" />
            <Campo rotulo="Telefone" valor={c.telefone} mudar={campo('telefone')} modo="tel" />
          </div>
          <Campo rotulo="Cidade/UF" valor={c.cidade} mudar={campo('cidade')} />
          {gestor && <button className="botao" disabled={indo} onClick={salvar}>{indo ? 'Salvando…' : 'Salvar dados da empresa'}</button>}
        </fieldset>
        <Dica>Aparece no cabeçalho do romaneio (como comprador nas compras), no WhatsApp e no relatório de custo.{gestor ? '' : ' Só o gestor muda estes dados.'}</Dica>
      </div>
    </>
  )
}
